import type { TApiImage } from './apiComponentTypes';

export type TImageFormatName = 'thumbnail' | 'small' | 'medium' | 'large';
export type TImageFormat = TImageFormatName | 'original';

type TImageFormatData = { url: string; width: number; height: number };

export type TSelectedImageSource = TImageFormatData & {
  alt: string;
  format: TImageFormat;
};

const formatFallbackOrder: Record<TImageFormat, TImageFormat[]> = {
  thumbnail: ['thumbnail', 'small', 'medium', 'large', 'original'],
  small: ['small', 'medium', 'large', 'original'],
  medium: ['medium', 'large', 'small', 'original'],
  large: ['large', 'medium', 'small', 'original'],
  original: ['original', 'large', 'medium', 'small', 'thumbnail'],
};

const getFormatData = (image: TApiImage['src'], format: TImageFormat): TImageFormatData | null => {
  if (format === 'original') {
    return image.url
      ? {
          url: image.url,
          width: image.width,
          height: image.height,
        }
      : null;
  }

  const formatData = image.formats?.[format];

  if (formatData?.url) {
    return formatData;
  }

  return null;
};

export const getImageSource = (
  image: TApiImage['src'] | null | undefined,
  preferredFormat: TImageFormat | TImageFormat[] = 'medium'
): TSelectedImageSource | null => {
  if (!image) {
    return null;
  }

  const formatsToTry = Array.isArray(preferredFormat)
    ? Array.from(
        new Set(
          preferredFormat.reduce<TImageFormat[]>((acc, current) => acc.concat(formatFallbackOrder[current]), [] as TImageFormat[])
        )
      )
    : formatFallbackOrder[preferredFormat];

  for (const format of formatsToTry) {
    const formatData = getFormatData(image, format);

    if (formatData) {
      return {
        ...formatData,
        alt: image.alternativeText || image.name || 'Zdjęcie',
        format,
      };
    }
  }

  return null;
};
